import { ReactNode, useContext } from "react";

import { Text, TextProps } from "../Text";
import { Tooltip } from "../Tooltip/Tooltip";
import { Icon } from "../Icon";
import { SvgInfo } from "../Icon/icons";
import { Flex } from "../Flex";
import { FormControlContext } from "./FormControlContext";
import { CSS } from "~/application/theme";
import useMobile from "~/presentation/shared/hooks/useMobile";

export type FieldLabelProps = {
  children: ReactNode;
  css?: CSS;
  withRules?: boolean;
  variant?: TextProps["variant"];
  size?: TextProps["size"];
  fw?: TextProps["fw"];
  htmlFor?: string;
  tooltip?: ReactNode;
  required?: boolean;
};

type RequiredSpanProps = {
  isMobile: boolean;
};

export const RequiredSpan = ({ isMobile }: RequiredSpanProps) => {
  const labelContent = isMobile ? "*" : "Obrigatório";

  return (
    <Text
      as="label"
      size="2"
      variant="warning-dark"
      css={{ fw: "700", ml: "$2", userSelect: "none" }}
    >
      {labelContent}
    </Text>
  );
};

export const FieldLabel = ({
  children,
  css,
  withRules = true,
  variant = "darkest",
  size = "2",
  fw = "700",
  htmlFor,
  tooltip,
  required,
}: FieldLabelProps) => {
  const formControl = useContext(FormControlContext);
  const isMobile = useMobile();
  const isRequired = required ?? (withRules && formControl.rules?.required);

  return (
    <Flex align="center" gap="1" css={{ mb: "$2", width: "fit-content", ...css }}>
      <Text
        as="label"
        htmlFor={htmlFor || formControl.name}
        size={size}
        variant={variant}
        fw={fw}
        css={{ display: "block" }}
      >
        {children}
        {isRequired && <RequiredSpan {...{ isMobile }} />}
      </Text>
      {tooltip && (
        <Tooltip content={tooltip}>
          <Icon as={SvgInfo} size="sm" css={{ cursor: "help", color: "$primary-base" }} />
        </Tooltip>
      )}
    </Flex>
  );
};

FieldLabel.displayName = "FieldLabel";
